import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { CgMenuGridO } from 'react-icons/cg';
import { FiBookOpen, FiGrid } from 'react-icons/fi';

export default function ProductsNavMenu({ activeNav, onNavigate }) {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState({ top: 0, left: 0 });
  const triggerRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  const updateMenuPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setMenuPos({
      top: Math.round(rect.bottom + 6),
      left: Math.round(rect.left),
    });
  }, []);

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const close = () => setOpen(false);

  const menuLayer =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div className="products-nav-menu-layer" data-products-nav-menu-layer>
            <button
              type="button"
              className="products-nav-menu-backdrop"
              aria-label="Close menu"
              tabIndex={-1}
              onClick={close}
            />
            <div
              ref={dropdownRef}
              className="products-nav-menu-dropdown products-nav-menu-dropdown--portal"
              role="menu"
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              <Link
                to="/products"
                role="menuitem"
                className={`products-nav-menu-item${activeNav === 'products' ? ' products-nav-menu-item--active' : ''}`}
                onClick={() => {
                  close();
                  onNavigate?.();
                }}
              >
                <FiGrid className="products-nav-menu-item-icon" aria-hidden />
                <span>Products</span>
              </Link>
              <Link
                to="/docs"
                role="menuitem"
                className={`products-nav-menu-item${activeNav === 'docs' ? ' products-nav-menu-item--active' : ''}`}
                onClick={() => {
                  close();
                  onNavigate?.();
                }}
              >
                <FiBookOpen className="products-nav-menu-item-icon" aria-hidden />
                <span>Documentation</span>
              </Link>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="products-nav-menu">
      <button
        ref={triggerRef}
        type="button"
        className={`products-nav-menu-trigger${open ? ' products-nav-menu-trigger--open' : ''}`}
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            if (next) {
              requestAnimationFrame(() => updateMenuPosition());
            }
            return next;
          });
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Site menu"
      >
        <CgMenuGridO size={22} aria-hidden />
      </button>
      {menuLayer}
    </div>
  );
}
